import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../filtres/filtres.service';
import moment from 'moment';

const logsEndpoint = `${environment.basePath}/logs/logs`;
const logsUtilsGetEndpoint = `${environment.basePath}/logs/utils/get`;

export interface LogObject {
  action: ActionEnum;
  result: ResultEnum;
  typeTarget: TypeTargetEnum;
  idTarget?: string;
  subDomain?: string;
  details?: string;
  internals?: any;
}

export class LogFonctionnelCallerService {

  private _typeActor: TypeActorEnum;
  private _domain: DomainEnum;
  private _httpClient: HttpClient;

  constructor(
    httpClient: HttpClient,
    typeActor: TypeActorEnum,
    domain: DomainEnum
  ) {
    this._httpClient = httpClient;
    this._typeActor = typeActor;
    this._domain = domain;
  }

  public async addLog(log: LogObject): Promise<void> {
    return new Promise<void>(async (resolve) => {
      try {
        const logRqt: LogFonctionnelPost = {
          action: log.action,
          domain: this._domain,
          idActor: this._getActorId() || '',
          idTarget: log.idTarget,
          result: log.result,
          typeActor: this._typeActor,
          typeTarget: log.typeTarget,
          subDomain: log.subDomain,
          detail: log.details,
          internals: log.internals
        };
        await this._addLog(logRqt);
        resolve();
      } catch (e) {
        console.error(e);
      }
    });
  }

  private async _addLog(log: LogFonctionnelPost): Promise<void> {
    try {
      await this._httpClient.post(
        logsEndpoint,
        log
      ).toPromise();
    } catch (e) {
      console.error(e);
    }
  }

  private _getActorId(): string {
    let rtd: string | undefined = undefined;

    const currentProfilStr = localStorage.getItem('currentProfil');
    if (currentProfilStr && currentProfilStr !== '') {
      const currentProfil = JSON.parse(currentProfilStr);
      rtd = currentProfil.id && currentProfil.id !== '' ? currentProfil.id : undefined;
    }

    return rtd;
  }
}

@Injectable({
  providedIn: 'root'
})
export class LogFonctionnelService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  constructor(
    private _httpClient: HttpClient,
    private _filtresService: FiltresService,
  ) { }

  public getConfiguration(domain: DomainEnum): LogFonctionnelCallerService {
    return new LogFonctionnelCallerService(this._httpClient, this._getTypeActor(), domain);
  }

  public async getLogs(params: LogFonctionnelFilter, pageFiltres: Filtres = {
    perpage: 30,
    currentPage: 1,
    sortOrder: SortEnum.DESC,
    sortBy: 'timestamp'
  }): Promise<Paginated<LogFonctionnelGet>> {
    return new Promise<Paginated<LogFonctionnelGet>>(async (resolve) => {
      try {
        const options = {...this.httpOptions};
        options.headers = this._filtresService.setHeaders(pageFiltres)

        let httpParams = new HttpParams();

        if (params.domain) {
          httpParams = httpParams.append('LogFonctionnelFilterDomain', params.domain);
        }

        if (params.idTarget) {
          httpParams = httpParams.append('LogFonctionnelFilterIdTarget', params.idTarget);
        }

        if (params.typeTarget) {
          httpParams = httpParams.append('LogFonctionnelFilterTypeTarget', params.typeTarget);
        }

        if (params.result) {
          httpParams = httpParams.append('LogFonctionnelFilterResult', params.result);
        }

        if (params.dateStart) {
          httpParams = httpParams.append('LogFonctionnelFilterDateStart', moment(params.dateStart).format('YYYY-MM-DD'));
        }

        if (params.dateEnd) {
          httpParams = httpParams.append('LogFonctionnelFilterDateEnd', moment(params.dateEnd).format('YYYY-MM-DD'));
        }

        if (params.action) {
          httpParams = httpParams.append('LogFonctionnelFilterAction', params.action);
        }

        options.params = httpParams;

        const res = await this._httpClient.get<Array<LogFonctionnelGet>>(
          logsEndpoint,
          options
        ).toPromise();

        const paginated: Paginated<LogFonctionnelGet> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: res.body
        };
        resolve(paginated);
      } catch (e) {
        console.error(e);
      }
    });
  }

  public async getActionsFromDomain(domain: DomainEnum): Promise<Array<string>> {
    try {
      const res = await this._httpClient.get<Array<string>>(
        `${logsUtilsGetEndpoint}/actions`, {
          params: {
            domain: domain
          }
        }
      ).toPromise();
      return res;
    } catch (e) {
      console.error(e);
    }
  }

  private _getTypeActor(): TypeActorEnum {
    let typeActor: TypeActorEnum = TypeActorEnum.Unknown;

    const currentUserStr = localStorage.getItem('currentUser');
    if (currentUserStr && currentUserStr !== '') {
      const currentUser = JSON.parse(currentUserStr);
      if (currentUser.admin) {
        typeActor = TypeActorEnum.Admin;
      } else {
        typeActor = TypeActorEnum.Utilisateur;
      }
    }

    return typeActor;
  }
}

export interface LogFonctionnelPost {
  idActor: string;
  typeActor: TypeActorEnum;
  idTarget?: string;
  typeTarget: TypeTargetEnum;
  action: ActionEnum;
  result: ResultEnum;
  domain: DomainEnum;
  subDomain?: string;
  subSubDomain?: string;
  detail?: string;
  internals?: any;
}

export enum TypeActorEnum {
  Utilisateur = 'UTILISATEUR',
  Admin = 'ADMIN',
  Unknown = 'UNKNOWN',
}

export enum TypeTargetEnum {
  Patient = 'PATIENT',
  Correspondant = 'CORRESPONDANT',
  Utilisateur = 'UTILISATEUR',
  Cabinet = 'CABINET',
  NONE = 'NONE',
}

export enum ActionEnum {
  Create = 'CREATE',
  Read = 'READ',
  Update = 'UPDATE',
  Delete = 'DELETE',
}

export enum ResultEnum {
  Success = 'SUCCESS',
  Error = 'ERROR',
  Unknown = 'UNKNOWN',
}

export enum DomainEnum {
  Agenda = 'AGENDA',
  Consultation = 'CONSULTATION',
  Correspondant = 'CORRESPONDANT',
  Document = 'DOCUMENT',
  Facturation = 'FACTURATION',
  Medical = 'MEDICAL',
  Messagerie = 'MESSAGERIE',
  Patient = 'PATIENT',
  Prescription = 'PRESCRIPTION',
  Settings = 'SETTINGS',
  Insi = 'INSI',
  Dmp = 'DMP',
  Eprescription = 'EPRESCRIPTION',
}

export interface LogFonctionnelFilter {
  idTarget?: string;
  typeTarget?: string;
  domain?: string;
  result?: string;
  dateStart?: Date;
  dateEnd?: Date;
  action?: string;
}

export interface LogFonctionnelGet {
  uuid?: string;
  idActor: string;
  typeActor: string;
  idTarget?: string;
  typeTarget: string;
  action: string;
  result: string;
  domain: string;
  subDomain?: string;
  subSubDomain?: string;
  detail?: string;
  rpps?: string;
  timestamp?: Date;
}

